import { createApp } from "vue";
import "vuetify/styles";
import "@mdi/font/css/materialdesignicons.css";
import "material-design-icons-iconfont/dist/material-design-icons.css";
import { createVuetify } from "vuetify";
import type { ThemeDefinition } from "vuetify";
import { aliases, mdi } from "vuetify/iconsets/mdi";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "@/config/vee-validate";
import "@/styles/app.scss"; // Import your global SCSS file

import { nl, en } from "vuetify/locale";

import App from "@/App.vue";
import router from "@/router/index.ts";

//thhird party
import masonry from "vue-next-masonry";
import { configure } from "vee-validate";
import Toast from "vue-toastification";

//app configuration
import "vue-toastification/dist/index.css";
import { IAppConfiguration } from "IAppConfiguration";
import { getConfiguration, createStore } from "@/stores/appconfig";
import { useAuthStore } from "@/stores/auth";
import httpClient from "@/services/api/httpClient.js";
import httpClientUnauthorized from "@/services/api/httpClientUnauthorized";

//vuetify labs components
import { VDateInput } from "vuetify/labs/VDateInput";
import { VStepperVertical } from "vuetify/labs/VStepperVertical";

// register components
import InnBreadcrumbs from "@/components/general/InnBreadcrumbs.vue";
import BasePage from "@/components/general/BasePage.vue";
import InformationDropdown from "@/components/general/InformationDropdown.vue";
import WarningDropdown from "@/components/general/WarningDropdown.vue";
import LoaderSpinner from "@/components/general/LoaderSpinner.vue";
import FilterSet from "@/components/general/filters/FilterSet.vue";
import FilterItem from "@/components/general/filters/FilterItem.vue";
import EnergyLabel from "@/components/general/EnergyLabel.vue";
import GridPagination from "@/components/general/GridPagination.vue";
import VueDatePicker from '@vuepic/vue-datepicker';

//urls
const hostUrl = window.location.origin + "/";
const portalUrl = import.meta.env.VITE_VUE_APP_PORTAL_URL ? import.meta.env.VITE_VUE_APP_PORTAL_URL : hostUrl;
const signalRUrl = import.meta.env.VITE_SIGNALR_URL ?? hostUrl;
configure({
  validateOnInput: true,
});

const icons = {
  defaultSet: "mdi",
  aliases,
  sets: {
    mdi,
  },
};

const toastOptions = {
  position: "bottom-right",
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: true,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
};

const innaxLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    primary: "#00a4e0",
    secondary: "#00a4e0",
    info: "#00a4e0",
    success: "#21c45d",
    warning: "#f59f0a",
    error: "#ef4343",
    background: "#ffffff",
    surface: "#ffffff",
    "on-background": "#3b515e",
    "on-surface": "#3b515e",
    outline: "#e5e5e5",
  },
  variables: {
    "border-color": "#e5e5e5",
    "border-opacity": 0.12,
    "high-emphasis-opacity": 1,
    "medium-emphasis-opacity": 0.75,
    "disabled-opacity": 0.5,
    "hover-opacity": 0.04,
    "focus-opacity": 0.08,
    "selected-opacity": 0.08,
    "activated-opacity": 0.12,
    "pressed-opacity": 0.12,
    "dragged-opacity": 0.08,
    "theme-kbd": "#212529",
    "theme-on-kbd": "#ffffff",
    "theme-code": "#f5f5f5",
    "theme-on-code": "#000000",
  },
};

const innaxDarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    primary: "#00a4e0",
    secondary: "#00a4e0",
    info: "#00a4e0",
    success: "#21c45d",
    warning: "#f59f0a",
    error: "#ef4343",
    background: "#19191a",
    surface: "#19191a",
    "on-background": "#fafafa",
    "on-surface": "#fafafa",
    outline: "#282828",
  },
  variables: {
    "border-color": "#282828",
    "border-opacity": 0.12,
    "high-emphasis-opacity": 1,
    "medium-emphasis-opacity": 0.75,
    "disabled-opacity": 0.5,
    "hover-opacity": 0.04,
    "focus-opacity": 0.08,
    "selected-opacity": 0.08,
    "activated-opacity": 0.12,
    "pressed-opacity": 0.12,
    "dragged-opacity": 0.08,
    "theme-kbd": "#212529",
    "theme-on-kbd": "#ffffff",
    "theme-code": "#343434",
    "theme-on-code": "#fafafa",
  },
};

getConfiguration(hostUrl, portalUrl, signalRUrl).then((appConfig) => {
  const vuetify = createVuetify({
    components: {
      ...components,
      VStepperVertical,
      VDateInput,
    },
    directives,
    icons,
    locale: {
      locale: "nl",
      fallback: "en",
      messages: { nl },
    },
    theme: {
      defaultTheme: "innaxLightTheme",
      themes: {
        innaxLightTheme,
        innaxDarkTheme,
      },
    },
  });

  const app = createApp(App).use(createStore(appConfig));

  app.config.errorHandler = (err, instance, info) => {
    console.error('[Global errorHandler]', { err, instance, info })
  }

  const authStore = useAuthStore();
  authStore.init(appConfig);

  app.use(router).use(vuetify).use(masonry).use(Toast, toastOptions).use(httpClient, { portalUrl: appConfig.portalUrl }).use(httpClientUnauthorized, { portalUrl: appConfig.portalUrl });

  app.component("BasePage", BasePage);
  app.component("inn-breadcrumbs", InnBreadcrumbs);
  app.component("information-dropdown", InformationDropdown);
  app.component("warning-dropdown", WarningDropdown);
  app.component("loader-spinner", LoaderSpinner);
  app.component("filter-set", FilterSet);
  app.component("filter-item", FilterItem);
  app.component("energy-label", EnergyLabel);
  app.component("grid-pagination", GridPagination);
  app.component('VueDatePicker', VueDatePicker);

  app.mount("#app");
});
